/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { createEventEmitter, emitEvent } from "@adapptio/lib-shared";
import { Dialog } from "../Dialog";
import { ConfirmationDialogComponent } from "./ConfirmationDialogComponent";
import { ACTION_BUTTON_STYLE } from "../../ui/components/ActionButton";

/**
 * Confirmation Dialog Parameters
 */
export interface ConfirmationDialog_Params {
	/** Dialog title */
	title: string;
	/** Message */
	message: string;
	/** Icon */
	icon?: string;
	/** Translation variables passed to title and message */
	translateVars?: { [K: string]: string | number | boolean };

	/** If set user will be asked to enter this value into a text input
	 * and confirm button will enabled only if value matches
	 */
	confirmInput?: string;

	confirmIcon?: string;
	confirmLabel?: string;
	confirmStyle?: ACTION_BUTTON_STYLE;

	cancelIcon?: string;
	cancelLabel?: string;
	cancelStyle?: ACTION_BUTTON_STYLE;

	secondConfirmIcon?: string;
	secondConfirmLabel?: string;
	secondConfirmStyle?: ACTION_BUTTON_STYLE;

	/**
	 * Optional functions to be called on confirm before the dialog is closed.
	 * Until promise is resolved, the loading is displayed and buttons are disabled.
	 */
	doConfirm?: () => Promise<void>;

	/**
	 * Optional functions to be called on seccond confirm before the dialog is closed.
	 * Until promise is resolved, the loading is displayed and buttons are disabled.
	 */
	doSecondConfirm?: () => Promise<void>;
}

/**
 * Confirmation Dialog
 * Allows to ask user for a confirmation.
 */
export class ConfirmationDialog extends Dialog<ConfirmationDialog_Params, boolean> {
	/** Working state */
	private working: boolean;

	/** Emitted when working state has changed */
	public onWorkingStateChange = createEventEmitter<void>();

	/**
	 * If confirm action is in progress
	 */
	public isWorking(): boolean {
		return this.working;
	}

	/**
	 * Confirms action
	 */
	public confirm(): void {
		if (this.working) {
			return;
		}

		if (this.params.doConfirm) {
			this.working = true;
			emitEvent(this.onWorkingStateChange);

			this.params
				.doConfirm()
				.then(() => {
					this.working = false;
					emitEvent(this.onWorkingStateChange);
					this.close(true);
				})
				.catch(() => {
					this.working = false;
					emitEvent(this.onWorkingStateChange);
				});
		} else {
			this.close(true);
		}
	}

	/**
	 * Seccond confirms action
	 */
	public secondConfirm(): void {
		if (this.working) {
			return;
		}

		if (this.params.doSecondConfirm) {
			this.working = true;
			emitEvent(this.onWorkingStateChange);

			this.params
				.doSecondConfirm()
				.then(() => {
					this.working = false;
					emitEvent(this.onWorkingStateChange);
					this.close(true);
				})
				.catch(() => {
					this.working = false;
					emitEvent(this.onWorkingStateChange);
				});
		} else {
			this.close(true);
		}
	}

	public getComponent(): typeof ConfirmationDialogComponent {
		return ConfirmationDialogComponent;
	}
}
